/**
 * The emoji pool (ADR-114 decision 5.5): at most `EMOJI_POOL_SIZE` sprites, each held by one character's handle. A new
 * emoji from a character that holds a sprite takes that sprite and starts its time again; one from another takes a
 * free sprite, else a new one while fewer than the pool's size exist, else the sprite of the emoji started longest
 * ago. The slots are fixed arrays searched in order, so starting, finding and ending an emoji allocates nothing once
 * the sprites are made, and nothing is made until the first emoji.
 *
 * @example
 * ```ts
 * const pool = new EmojiPool(() => makeSprite());
 * const sprite = pool.start(handle, now);
 * pool.forEachActive((held, handle, startedAt) => draw(held, handle, now - startedAt));
 * pool.end(handle);
 * ```
 */

/**
 * How many emoji are drawn at once at most, as the bubbles' pool holds 32 (the `chat-bubbles` card).
 */
export const EMOJI_POOL_SIZE = 32;

/**
 * The largest handle a snapshot carries.
 */
const MAX_HANDLE = 0xffff;

/**
 * A slot no character holds.
 */
const FREE = -1;

/**
 * A pool of emoji sprites, one a character.
 */
export class EmojiPool<T> {
  private readonly sprites: (T | undefined)[];
  private readonly handles: Int32Array;
  private readonly starts: Float64Array;
  private made = 0;
  private held = 0;

  /**
   * @param create Makes a sprite, when an emoji comes and no made sprite is free and fewer than the pool's size exist.
   * @param size The most sprites the pool makes.
   */
  public constructor(
    private readonly create: () => T,
    private readonly size: number = EMOJI_POOL_SIZE,
  ) {
    this.sprites = new Array<T | undefined>(size).fill(undefined);
    this.handles = new Int32Array(size).fill(FREE);
    this.starts = new Float64Array(size);
  }

  /**
   * Emoji held by characters now.
   *
   * @returns The count.
   */
  public get active(): number {
    return this.held;
  }

  /**
   * Sprites the factory has made, at most the pool's size.
   *
   * @returns The count.
   */
  public get created(): number {
    return this.made;
  }

  /**
   * Starts a character's emoji: the sprite it holds, else a free one, else a new one, else the one started longest ago,
   * whose character then holds none.
   *
   * @param handle The character's handle.
   * @param at The scene time the emoji starts, in ms.
   * @returns The sprite.
   * @throws RangeError when the handle is not one a snapshot carries (0 to 65535).
   */
  public start(handle: number, at: number): T {
    if (!Number.isInteger(handle) || handle < 0 || handle > MAX_HANDLE) {
      throw new RangeError(`an emoji's handle must be a whole number from 0 to ${MAX_HANDLE}, not ${handle}`);
    }
    let slot = this.slotOf(handle);
    if (slot === FREE) {
      slot = this.freeSlot();
      if (slot === FREE) {
        slot = this.oldestSlot();
      } else {
        this.held += 1;
      }
    }
    this.handles[slot] = handle;
    this.starts[slot] = at;
    let sprite = this.sprites[slot];
    if (sprite === undefined) {
      sprite = this.create();
      this.sprites[slot] = sprite;
      this.made += 1;
    }
    return sprite;
  }

  /**
   * The sprite a character holds.
   *
   * @param handle The character's handle.
   * @returns The sprite, or undefined when it holds none.
   */
  public find(handle: number): T | undefined {
    const slot = this.slotOf(handle);
    return slot === FREE ? undefined : this.sprites[slot];
  }

  /**
   * Ends a character's emoji, freeing its sprite for the next.
   *
   * @param handle The character's handle.
   * @returns The sprite it held, so the caller can hide it, or undefined when it held none.
   */
  public end(handle: number): T | undefined {
    const slot = this.slotOf(handle);
    if (slot === FREE) {
      return undefined;
    }
    this.handles[slot] = FREE;
    this.held -= 1;
    return this.sprites[slot];
  }

  /**
   * Visits every emoji held, in slot order. A visit may end the emoji it is told of, or any other.
   *
   * @param visit Told each sprite, its character's handle and when its emoji started.
   */
  public forEachActive(visit: (sprite: T, handle: number, startedAt: number) => void): void {
    for (let slot = 0; slot < this.size; slot += 1) {
      const handle = this.handles[slot] as number;
      if (handle !== FREE) {
        visit(this.sprites[slot] as T, handle, this.starts[slot] as number);
      }
    }
  }

  /**
   * The slot a character holds.
   *
   * @param handle The character's handle.
   * @returns The slot, or `FREE`.
   */
  private slotOf(handle: number): number {
    for (let slot = 0; slot < this.size; slot += 1) {
      if (this.handles[slot] === handle) {
        return slot;
      }
    }
    return FREE;
  }

  /**
   * A slot no character holds, a made sprite's before an unmade one's.
   *
   * @returns The slot, or `FREE` when every slot is held.
   */
  private freeSlot(): number {
    let unmade = FREE;
    for (let slot = 0; slot < this.size; slot += 1) {
      if (this.handles[slot] === FREE) {
        if (this.sprites[slot] !== undefined) {
          return slot;
        }
        if (unmade === FREE) {
          unmade = slot;
        }
      }
    }
    return unmade;
  }

  /**
   * The held slot whose emoji started longest ago, the first of equals.
   *
   * @returns The slot.
   */
  private oldestSlot(): number {
    let oldest = 0;
    for (let slot = 1; slot < this.size; slot += 1) {
      if ((this.starts[slot] as number) < (this.starts[oldest] as number)) {
        oldest = slot;
      }
    }
    return oldest;
  }
}
